import type { CauseDeLaDate, DossierVu } from '../partage/api/types';
import { enFrancais, jourEtHeureDUnInstant } from '../partage/datesEnMots';
import { finAuPlusTot, PERSONNE_N_A_DONNE_DE_DELAI } from '../partage/vocabulaire';

/**
 * Les mots d'un projet, sans rendu (chantier nettoyage-regles, ticket 02, sortis de « Projets ») :
 * le mot d'état, « Fin au plus tôt » et l'énoncé. Lus par « Projets » (`projets/Dossiers.tsx`) et
 * la tête d'un projet au téléphone (`projets/EtatDuProjet.tsx`).
 */

/**
 * Le mot d'état d'un projet : ambre s'il attend le monde, gris sinon, jamais de rouge. Lu par
 * le téléphone (`EtatDuProjet`).
 */
export function motsDEtat(dossier: DossierVu): { attendLeMonde: boolean; mots: string } {
  if (dossier.faits.enAttente > 0) return { attendLeMonde: true, mots: 'Attend le monde' };
  if (dossier.dateAuPlusTot.sorte === 'quand_tu_veux') {
    return { attendLeMonde: false, mots: 'Jamais bloqué, aucune date' };
  }
  return { attendLeMonde: false, mots: 'Rien ne bloque' };
}

/**
 * « Fin au plus tôt », en deux lignes : la réponse, puis ce qui la rend telle.
 *
 * Sous une date, la bulle qui la retient : « les billets d'abord ». Elle se tait quand le
 * projet se retient lui-même ou quand c'est l'attente qui n'est jamais revenue. « Pas encore
 * calculable » sans sa raison ressemblerait à une panne de l'app : sa raison est toujours la
 * même, personne n'a donné de délai.
 */
export function motsDuPlusTot(dossier: DossierVu): {
  phrase: string;
  precision: string | null;
  cause: string | null;
  ferme: boolean;
} {
  const date = dossier.dateAuPlusTot;
  const phrase = finAuPlusTot(date);
  switch (date.sorte) {
    case 'date':
      return { phrase, precision: null, cause: causeEnMots(dossier, date.cause), ferme: true };
    case 'quand_tu_veux':
      return { phrase, precision: null, cause: null, ferme: false };
    default:
      return { phrase, precision: PERSONNE_N_A_DONNE_DE_DELAI, cause: null, ferme: false };
  }
}

/** « les billets d'abord » : le titre tel qu'Igor l'a écrit, jamais réécrit ni converti. */
function causeEnMots(dossier: DossierVu, cause: CauseDeLaDate): string | null {
  const titre = cause.titre.trim();
  if (!titre || cause.id === dossier.id) return null;
  if (titre === dossier.faits.premiereRelance?.titre.trim()) return null;
  return `${titre} d’abord`;
}

/**
 * L'ÉNONCÉ d'un projet en une phrase : ce qui attend le monde se dit sans reproche, « n'est
 * jamais revenu », jamais « en retard » (principe 3), et en ambre. Dans « Projets », la
 * colonne « Maintenant » le dit quand le projet n'a pas de prochaine chose ; le téléphone
 * (`EtatDuProjet`) le lit tel quel.
 */
export function cePassage(dossier: DossierVu): {
  ton: 'monde' | 'toi';
  phrase: string;
  precision: string | null;
} {
  const { relances, faisables, enAttente, premiereRelance, prochainRendezVous } = dossier.faits;

  if (relances > 0 && premiereRelance) {
    return {
      ton: 'monde',
      phrase: `${premiereRelance.titre} n’est jamais revenu.`,
      precision: depuisQuand(premiereRelance),
    };
  }

  if (prochainRendezVous) {
    const quand = jourEtHeureDUnInstant(new Date(prochainRendezVous.debut));
    return { ton: 'toi', phrase: `${prochainRendezVous.titre} ${quand}.`, precision: null };
  }

  if (faisables === 0 && enAttente > 0) {
    return { ton: 'monde', phrase: 'Tout est chez les autres.', precision: null };
  }
  if (faisables === 0) return { ton: 'toi', phrase: 'Rien à faire ici pour l’instant.', precision: null };
  if (faisables === 1) return { ton: 'toi', phrase: 'Une seule chose faisable tout de suite.', precision: null };
  if (faisables > 8) {
    return { ton: 'toi', phrase: `${faisables} choses possibles, aucune urgence.`, precision: null };
  }
  return { ton: 'toi', phrase: `${faisables} choses faisables tout de suite.`, precision: null };
}

/** « déposé le 3 mars, retour attendu le 2 avril » : les deux dates quand on les a. */
function depuisQuand(relance: { dateAction: string | null; attenduLe: string | null }): string | null {
  if (relance.dateAction && relance.attenduLe) {
    return `Déposé le ${enFrancais(relance.dateAction)}, retour attendu le ${enFrancais(relance.attenduLe)}`;
  }
  if (relance.dateAction) return `Demandé le ${enFrancais(relance.dateAction)}`;
  if (relance.attenduLe) return `Retour attendu le ${enFrancais(relance.attenduLe)}`;
  return null;
}
